/**
 * Canvas Context Hook - Studio v3.6 Sprint 1 + Context Canvas v1
 *
 * Domain -> Canvas mapping abstraction.
 * Manages current canvas domain, transition state, and canvas mode.
 *
 * Per GUARD.md:
 * - Canvas changes must be narratively acknowledged by Soma
 * - No silent visual changes
 *
 * Context Canvas v1 Contract:
 * - Canvas types: idle | browser | detail | automation
 * - Card links open in center panel, not new tabs
 * - Single openInContextCanvas() dispatcher for all card actions
 * - Session-scoped canvas state (not global)
 */

import { create } from 'zustand';

/**
 * Canvas type - determines what the center panel renders
 * - idle: Default calm state with HiveCore logo
 * - browser: Embedded iframe for external URLs (GitHub, Railway, Netlify, docs)
 * - detail: Full artifact/log view for card "More details" actions
 * - automation: ABDA-driven browser stream for automation tasks
 */
export type CanvasType = 'idle' | 'browser' | 'detail' | 'automation';

/**
 * Canvas payload - data for the current canvas view
 */
export interface CanvasPayload {
    /** URL for browser mode */
    url?: string;
    /** Title for the canvas view */
    title?: string;
    /** Artifact data for detail mode */
    artifact?: {
        type: 'pr' | 'deploy' | 'log' | 'mission' | 'run' | 'generic';
        id?: string;
        data?: Record<string, unknown>;
    };
    /** Stream ID for automation mode */
    streamId?: string;
    /** Whether CSP blocked iframe embedding (triggers ABDA fallback) */
    cspBlocked?: boolean;
}

/**
 * Canvas Domain - represents a visual context in the center panel
 */
export interface CanvasDomain {
    /** Unique identifier for the domain */
    id: string;
    /** Display label */
    label: string;
    /** Optional description shown during transitions */
    description?: string;
    /** Optional logo URL */
    logoUrl?: string;
    /** App key for routing */
    appKey?: string;
    /** Route pattern */
    routePattern?: string;
}

/**
 * Predefined canvas domains
 * These map to the existing app structure
 */
export const CANVAS_DOMAINS: Record<string, CanvasDomain> = {
    // Core domains
    home: {
        id: 'home',
        label: 'HiveCore',
        description: 'System overview',
        logoUrl: '/assets/hivecore-logo.png',
    },

    // App domains
    aex: {
        id: 'aex',
        label: 'AEX',
        description: 'Trading & Execution',
        appKey: 'aex',
    },
    controlBridge: {
        id: 'control-bridge',
        label: 'Control Bridge',
        description: 'System orchestration',
        appKey: 'control-bridge',
    },
    sculptd: {
        id: 'sculptd',
        label: 'Sculptd',
        description: 'Design & prototyping',
        appKey: 'sculptd',
    },

    // HiveCore domains
    missionTimeline: {
        id: 'mission-timeline',
        label: 'Mission Timeline',
        description: 'Execution progress',
        appKey: 'mission-timeline',
        routePattern: '/hivecore/studio-v2/app/mission-timeline',
    },
    evoControl: {
        id: 'evo-control',
        label: 'EvoControl',
        description: 'Evolution management',
        appKey: 'evo-specs',
        routePattern: '/hivecore/studio-v2/app/evo-specs',
    },
    modelGovernance: {
        id: 'model-governance',
        label: 'Model Governance',
        description: 'AI model oversight',
        appKey: 'model-governance',
        routePattern: '/hivecore/studio-v2/app/model-governance',
    },

    // Diagnostic domains (Manual Mode)
    chatBuild: {
        id: 'chat-build',
        label: 'Chat Build Flow',
        description: 'Build pipeline diagnostics',
        appKey: 'chat-build',
        routePattern: '/hivecore/studio-v2/app/chat-build',
    },
    evoSignals: {
        id: 'evo-signals',
        label: 'Evo Signals',
        description: 'Signal ingestion',
        appKey: 'evo-signals',
        routePattern: '/hivecore/studio-v2/app/evo-signals',
    },
};

/**
 * Canvas context state
 */
interface CanvasContextState {
    /** Current domain being displayed */
    currentDomain: CanvasDomain | null;
    /** Previous domain (for transitions) */
    previousDomain: CanvasDomain | null;
    /** Whether a transition is in progress */
    isTransitioning: boolean;
    /** Transition start timestamp */
    transitionStartedAt: number | null;

    /** Context Canvas v1: Current canvas type */
    canvasType: CanvasType;
    /** Context Canvas v1: Current canvas payload */
    canvasPayload: CanvasPayload | null;
    /** Context Canvas v1: Whether canvas component is mounted (for fallback detection) */
    isCanvasMounted: boolean;

    /** Set the current domain with optional transition */
    setDomain: (domain: CanvasDomain | null, withTransition?: boolean) => void;
    /** Clear the current domain */
    clearDomain: () => void;

    /** Context Canvas v1: Open content in the canvas */
    openInCanvas: (type: CanvasType, payload?: CanvasPayload) => void;
    /** Context Canvas v1: Close the canvas and return to idle */
    closeCanvas: () => void;
    /** Context Canvas v1: Mark CSP blocked (triggers ABDA fallback) */
    markCspBlocked: () => void;
    /** Context Canvas v1: Set canvas mounted state */
    setCanvasMounted: (mounted: boolean) => void;
}

/**
 * Canvas context store
 */
export const useCanvasContextStore = create<CanvasContextState>((set, get) => ({
    currentDomain: null,
    previousDomain: null,
    isTransitioning: false,
    transitionStartedAt: null,
    canvasType: 'idle',
    canvasPayload: null,
    isCanvasMounted: false,

    setDomain: (domain, withTransition = true) => {
        const current = get().currentDomain;

        // Skip if same domain
        if (current?.id === domain?.id) {
            return;
        }

        if (withTransition && domain) {
            // Start transition
            set({
                previousDomain: current,
                currentDomain: domain,
                isTransitioning: true,
                transitionStartedAt: Date.now(),
            });

            // End transition after delay
            setTimeout(() => {
                set({
                    isTransitioning: false,
                    transitionStartedAt: null,
                });
            }, 200); // Match animation duration
        } else {
            // Immediate switch
            set({
                previousDomain: current,
                currentDomain: domain,
                isTransitioning: false,
                transitionStartedAt: null,
            });
        }
    },

    clearDomain: () => {
        set({
            previousDomain: get().currentDomain,
            currentDomain: null,
            isTransitioning: false,
            transitionStartedAt: null,
        });
    },

    openInCanvas: (type, payload) => {
        set({
            canvasType: type,
            canvasPayload: payload || null,
            isTransitioning: true,
        });

        // End transition after delay
        setTimeout(() => {
            set({ isTransitioning: false });
        }, 200);
    },

    closeCanvas: () => {
        set({
            canvasType: 'idle',
            canvasPayload: null,
            isTransitioning: false,
        });
    },

    markCspBlocked: () => {
        const current = get().canvasPayload;
        if (current) {
            set({
                canvasPayload: { ...current, cspBlocked: true },
            });
        }
    },

    setCanvasMounted: mounted => {
        set({ isCanvasMounted: mounted });
    },
}));

/**
 * Hook to access canvas context
 */
export function useCanvasContext() {
    const store = useCanvasContextStore();

    return {
        currentDomain: store.currentDomain,
        previousDomain: store.previousDomain,
        isTransitioning: store.isTransitioning,
        setDomain: store.setDomain,
        clearDomain: store.clearDomain,
        // Context Canvas v1
        canvasType: store.canvasType,
        canvasPayload: store.canvasPayload,
        openInCanvas: store.openInCanvas,
        closeCanvas: store.closeCanvas,
        markCspBlocked: store.markCspBlocked,
        setCanvasMounted: store.setCanvasMounted,
    };
}

/**
 * Context Canvas v1: Single dispatcher for opening content in the canvas
 * This is the primary API for card actions to route to the center panel
 *
 * Returns true if canvas is mounted and content was routed, false otherwise.
 * Callers should fall back to window.open() when this returns false.
 *
 * Canonical object signature (standardized across all callers):
 * - openInContextCanvas({ type: 'browser', url, title })
 * - openInContextCanvas({ type: 'detail', title, artifact: { type: 'pr', data: {...} } })
 * - openInContextCanvas({ type: 'automation', streamId: 'abda-session-123' })
 */
export function openInContextCanvas(options: { type: CanvasType } & CanvasPayload): boolean {
    const { type, ...payload } = options;
    const state = useCanvasContextStore.getState();
    if (!state.isCanvasMounted) {
        return false;
    }
    state.openInCanvas(type, payload);
    return true;
}

/**
 * Context Canvas v1: Close the canvas and return to idle state
 */
export function closeContextCanvas(): void {
    useCanvasContextStore.getState().closeCanvas();
}

/**
 * Get a domain by its ID
 */
export function getDomainById(id: string): CanvasDomain | undefined {
    return CANVAS_DOMAINS[id] || Object.values(CANVAS_DOMAINS).find(d => d.id === id);
}

/**
 * Get a domain by its app key
 */
export function getDomainByAppKey(appKey: string): CanvasDomain | undefined {
    return Object.values(CANVAS_DOMAINS).find(d => d.appKey === appKey);
}

export default useCanvasContext;
